import React, {useEffect, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import {ChevronDownIcon, ChevronRightIcon, PencilIcon, PlusIcon, Trash2Icon, User, Users} from "lucide-react";
import {useBusiness} from "../../../../../context/BusinessContext";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import Button from "../../../../../components/Button";
import Modal from "../../../../../components/Modal";
import SectionHeading from "../../../../../components/SectionHeading";
import {
    useCategories,
    useCreateCategory,
    useDeleteCategory,
    useUpdateCategory,
} from "../../../../../hooks/useTournaments";
import {useTournament} from "../../../../../hooks/useTournaments";
import {CompetitorType, PlayCategoryRequest, PlayCategoryResponse} from "../../../../../types/tournament";
import CategoryRegistrationForm from "./CategoryRegistrationForm";

type OutletCtx = {tournamentId: string | null; isEdit?: boolean};

type CatForm = {
    name: string;
    description: string;
    competitorType: CompetitorType;
    teamSize: string;
};

const EMPTY_CAT: CatForm = {name: "", description: "", competitorType: "INDIVIDUAL", teamSize: ""};

export function PlayCategoriesStep({mode}: {mode: "create" | "edit"}) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? "";

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const tournamentId =
        mode === "edit"
            ? (params.tournamentId ?? outlet.tournamentId ?? null)
            : (outlet.tournamentId ?? sp.get("tournamentId") ?? null);

    const {data: tournament} = useTournament(businessId, tournamentId || null);
    const currency = tournament?.currency ?? "NAD";

    const {data: categories, isLoading} = useCategories(businessId, tournamentId || null);
    const createMutation = useCreateCategory(businessId, tournamentId || null);
    const updateMutation = useUpdateCategory(businessId, tournamentId || null);
    const deleteMutation = useDeleteCategory(businessId, tournamentId || null);

    const list = categories ?? [];

    // Which category's registration panel is expanded.
    const [expandedId, setExpandedId] = useState<string | null>(null);

    // Add / edit modal state.
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState<CatForm>(EMPTY_CAT);

    // Delete confirm.
    const [deleteTarget, setDeleteTarget] = useState<PlayCategoryResponse | null>(null);

    // Keep the first category expanded once data arrives.
    useEffect(() => {
        if (expandedId == null && list.length > 0) setExpandedId(list[0].id);
    }, [list, expandedId]);

    const openCreate = () => {
        setEditingId(null);
        setForm(EMPTY_CAT);
        setModalOpen(true);
    };

    const openEdit = (c: PlayCategoryResponse) => {
        setEditingId(c.id);
        setForm({
            name: c.name ?? "",
            description: c.description ?? "",
            competitorType: c.competitorType ?? "INDIVIDUAL",
            teamSize: c.teamSize != null ? String(c.teamSize) : "",
        });
        setModalOpen(true);
    };

    const submitCategory = async () => {
        if (!form.name.trim()) {
            notify.error("Give the category a name.");
            return;
        }
        const teamSize =
            form.competitorType === "TEAM" && form.teamSize.trim() !== "" ? Number(form.teamSize) : null;
        if (teamSize != null && (!Number.isInteger(teamSize) || teamSize < 1)) {
            notify.error("Team size must be a whole number of 1 or more.");
            return;
        }

        const req: PlayCategoryRequest = {
            name: form.name.trim(),
            description: form.description.trim() || null,
            position: editingId
                ? (list.find((c) => c.id === editingId)?.position ?? list.length)
                : list.length,
            competitorType: form.competitorType,
            teamSize,
        };

        try {
            if (editingId) {
                await updateMutation.mutateAsync({categoryId: editingId, req});
                notify.success("Category updated");
            } else {
                const created = await createMutation.mutateAsync(req);
                notify.success("Category created");
                if (created?.id) setExpandedId(created.id);
            }
            setModalOpen(false);
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save category"));
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteMutation.mutateAsync(deleteTarget.id);
            notify.success("Category deleted");
            if (expandedId === deleteTarget.id) setExpandedId(null);
            setDeleteTarget(null);
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to delete category"));
        }
    };

    const goNext = () => {
        if (!tournamentId) return;
        if (mode === "edit") navigate(`/business/tournaments/${encodeURIComponent(tournamentId)}/edit/data-collection`);
        else navigate(`/business/tournaments/new/data-collection?tournamentId=${encodeURIComponent(tournamentId)}`);
    };

    if (!tournamentId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Details</span> first.
            </div>
        );
    }

    const inputCls =
        "mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none";

    return (
        <div className="space-y-6">
            <SectionHeading
                title="Play categories"
                description="Create the categories competitors enter (e.g. 2-a-side, 4-a-side). Each category has its own competitor type and registration settings."
                actions={
                    <Button type="button" variant="primary" size="md" onClick={openCreate}>
                        <PlusIcon className="size-4"/>
                        Add category
                    </Button>
                }
            />

            {isLoading ? (
                <div className="py-10 text-sm text-gray-500">Loading categories…</div>
            ) : list.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center">
                    <p className="text-sm font-semibold text-gray-900">No categories yet</p>
                    <p className="mt-1 text-sm text-gray-500">
                        Add at least one play category before competitors can register.
                    </p>
                    <div className="mt-4 flex justify-center">
                        <Button type="button" variant="primary" size="md" onClick={openCreate}>
                            <PlusIcon className="size-4"/>
                            Add your first category
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    {[...list].sort((a, b) => a.position - b.position).map((c) => {
                        const open = expandedId === c.id;
                        return (
                            <div key={c.id} className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                                <div className="flex items-center justify-between gap-3 p-4">
                                    <button
                                        type="button"
                                        onClick={() => setExpandedId(open ? null : c.id)}
                                        className="flex min-w-0 flex-1 items-center gap-3 text-left"
                                    >
                                        {open ? <ChevronDownIcon className="size-4 shrink-0 text-gray-400"/> :
                                            <ChevronRightIcon className="size-4 shrink-0 text-gray-400"/>}
                                        <span className="shrink-0 rounded-lg bg-gray-100 p-2 text-gray-500">
                                            {c.competitorType === "TEAM" ? <Users className="size-4"/> : <User className="size-4"/>}
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block truncate text-sm font-semibold text-gray-900">{c.name}</span>
                                            <span className="block truncate text-xs text-gray-500">
                                                {c.competitorType === "TEAM"
                                                    ? `Team${c.teamSize ? ` · ${c.teamSize} per side` : ""}`
                                                    : "Individual"}
                                                {" · "}{c.acceptedCount} accepted
                                            </span>
                                        </span>
                                    </button>
                                    <div className="flex shrink-0 items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() => openEdit(c)}
                                            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                            title="Edit category"
                                        >
                                            <PencilIcon className="size-4"/>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDeleteTarget(c)}
                                            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600"
                                            title="Delete category"
                                        >
                                            <Trash2Icon className="size-4"/>
                                        </button>
                                    </div>
                                </div>

                                {open ? (
                                    <div className="border-t border-gray-100 p-4">
                                        <CategoryRegistrationForm
                                            businessId={businessId}
                                            tournamentId={tournamentId}
                                            categoryId={c.id}
                                            competitorType={c.competitorType}
                                            currency={currency}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="flex items-center justify-end gap-3">
                <Button type="button" variant="secondary" size="md" onClick={() => navigate("/business/tournaments")}>
                    Exit
                </Button>
                <Button type="button" variant="primary" size="md" onClick={goNext} disabled={list.length === 0}>
                    Continue to Data Collection
                </Button>
            </div>

            {/* Add / edit category modal */}
            <Modal
                open={modalOpen}
                onClose={setModalOpen}
                title={editingId ? "Edit category" : "Add play category"}
                size="lg"
                submitting={createMutation.isPending || updateMutation.isPending}
                primaryText={editingId ? "Save category" : "Create category"}
                onPrimary={submitCategory}
            >
                <div className="space-y-4 text-left">
                    <div>
                        <label className="block text-sm font-medium text-gray-900">Category name *</label>
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm((p) => ({...p, name: e.target.value}))}
                            placeholder="e.g. 2-a-side"
                            className={inputCls}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-900">Description</label>
                        <textarea
                            rows={2}
                            value={form.description}
                            onChange={(e) => setForm((p) => ({...p, description: e.target.value}))}
                            placeholder="Optional — shown to competitors."
                            className={inputCls}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-900">Competitor type *</label>
                        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {([
                                {value: "INDIVIDUAL" as CompetitorType, label: "Individual", icon: <User className="size-4"/>},
                                {value: "TEAM" as CompetitorType, label: "Team", icon: <Users className="size-4"/>},
                            ]).map((opt) => {
                                const selected = form.competitorType === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setForm((p) => ({...p, competitorType: opt.value}))}
                                        className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition ${
                                            selected
                                                ? "border-orange-600 bg-orange-50 text-orange-700 ring-1 ring-orange-500"
                                                : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                                        }`}
                                    >
                                        {opt.icon}
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {form.competitorType === "TEAM" ? (
                        <div>
                            <label className="block text-sm font-medium text-gray-900">Team size</label>
                            <p className="mt-1 text-xs text-gray-500">Players per side (optional).</p>
                            <input
                                type="number"
                                min={1}
                                step={1}
                                value={form.teamSize}
                                onChange={(e) => setForm((p) => ({...p, teamSize: e.target.value}))}
                                placeholder="e.g. 2"
                                className={inputCls}
                            />
                        </div>
                    ) : null}
                </div>
            </Modal>

            {/* Delete confirm */}
            <Modal
                open={!!deleteTarget}
                onClose={(v) => !v && setDeleteTarget(null)}
                title="Delete category?"
                description={deleteTarget
                    ? `"${deleteTarget.name}" and its registration, competitors and competition will be removed. This cannot be undone.`
                    : undefined}
                size="sm"
                submitting={deleteMutation.isPending}
                primaryText="Delete"
                onPrimary={confirmDelete}
            />
        </div>
    );
}
